import { Message, MessageFile, useMessagesStore } from "@/entities/message";
import { useChatStore } from "@/entities/chat";
import { useScrollStore } from "@/features/chat/model";
import {
  removeTempMessageDB,
  updateTempMessageErrorDB,
} from "@/entities/message/lib";
import { showNotification } from "@/shared/ui";
import { sendWSMessage } from "..";
import { enqueueSnackbar } from "notistack";

export const handleMessageSended = (data: any, userId: string) => {
  if (!data.message) return;

  const message = data.message;
  const chatId = message.chat._id;
  const sentAt = new Date(message.sentAt).getTime();

  useMessagesStore.setState((state) => {
    const prevMessages = state.messages[chatId] || [];
    const prevFiles = state.files[chatId] || [];

    const tempMsg = prevMessages.find((m) => m.sentAt === sentAt);

    const finalMessage: Message = {
      ...message,
      fileUrls: message.fileUrls ?? tempMsg?.fileUrls ?? [],
      files: tempMsg?.files,
    };

    const withoutTemp = prevMessages.filter((m) => m.sentAt !== sentAt);
    const updatedMessages = [...withoutTemp, finalMessage].sort((a, b) => {
      const aTime = a.sentAt ?? a.createdAt;
      const bTime = b.sentAt ?? b.createdAt;
      return aTime - bTime;
    });

    let updatedFiles = [...prevFiles];

    const tempUrls = tempMsg?.fileUrls ?? [];
    const newUrls = message.fileUrls ?? [];

    if (tempUrls.length > 0 || newUrls.length > 0) {
      updatedFiles = updatedFiles.filter(
        (file) => !tempUrls.includes(file.url)
      );

      const newFileObjects: MessageFile[] = newUrls.map((url: string) => ({
        url,
        msgId: message._id,
        createdAt: message.createdAt,
      }));

      const mergedFiles = [
        ...updatedFiles,
        ...newFileObjects.filter(
          (nf) =>
            !updatedFiles.some(
              (f) => f.url === nf.url && f.msgId === nf.msgId
            )
        ),
      ];

      updatedFiles = mergedFiles;
    }

    if (finalMessage.owner._id !== userId && !finalMessage.delivedAt) {
      sendWSMessage("message:delived", {
        chat: finalMessage.chat._id,
        message: finalMessage._id,
        time: Date.now(),
      });
    }

    removeTempMessageDB(chatId, sentAt);

    return {
      messages: {
        ...state.messages,
        [chatId]: updatedMessages,
      },
      files: {
        ...state.files,
        [chatId]: updatedFiles,
      },
      loadingMessage: {
        ...state.loadingMessage,
        [finalMessage._id]: false,
      },
    };
  });

  useChatStore.setState((state) => {
    const chat = state.chats.find((c) => c._id === message.chat._id);

    if (
      message.owner._id !== userId &&
      state.currentChat?._id !== message.chat._id &&
      chat
    ) {
      const contactName =
        chat.contact.firstName || chat.contact.lastName
          ? `${chat.contact.firstName ?? ""} ${chat.contact.lastName ?? ""
            }`.trim()
          : `${chat.contact.user.firstName ?? ""} ${chat.contact.user.lastName ?? ""
            }`.trim();

      showNotification(contactName, message.text);
    }

    const chats = state.chats.map((chat) => {
      if (chat._id === message.chat._id) {
        return {
          ...chat,
          lastMessage: message,
        };
      }
      return chat;
    });

    return { chats };
  });

  if (message.owner._id !== userId) {
    const { unreadCount, setUnreadCount } = useScrollStore.getState();
    const current = unreadCount[message.chat._id] ?? 0;
    setUnreadCount(message.chat._id, current + 1);
    useChatStore.getState().updateUnreadCount(message.chat._id, current + 1);
  }
};

const handleMessageStatusUpdate = (
  message: Message,
  field: "delivedAt" | "readedAt"
) => {
  if (!message?._id || !message?.chat?._id) return;

  const messageTimestamp = new Date(message.createdAt).getTime();

  useMessagesStore.setState((state) => {
    const chatId = message.chat._id;
    const prevMessages = state.messages[chatId] || [];

    const updatedMessages = prevMessages.map((msg) => {
      if (new Date(msg.createdAt).getTime() <= messageTimestamp) {
        return {
          ...msg,
          [field]: msg[field] ?? message[field] ?? Date.now(),
        };
      }
      return msg;
    });

    return {
      messages: {
        ...state.messages,
        [chatId]: updatedMessages,
      },
    };
  });
};

export const handleMessageDeliveredResult = (data: any) => {
  if (data?.message) handleMessageStatusUpdate(data.message, "delivedAt");
};

export const handleMessageReadedResult = (data: any) => {
  if (data?.message) handleMessageStatusUpdate(data.message, "readedAt");
};

export const handleMessageDeleteResult = (data: any) => {
  if (!data?.result) return;

  const { ok: deletedMessages = [], bad: failedMessages = [] } = data.result;

  if (!Array.isArray(deletedMessages) || deletedMessages.length === 0) {
    if (failedMessages.length > 0) {
      enqueueSnackbar(`Не удалось удалить ${failedMessages.length} сообщений`, {
        variant: "warning",
      });
    }
    return;
  }

  const chatId = deletedMessages[0].chat._id;
  const messageIdsToDelete = deletedMessages.map((m) => m._id);

  useMessagesStore.setState((state) => {
    const prevMessages = state.messages[chatId] || [];
    const prevFiles = state.files[chatId] || [];

    const filteredMessages = prevMessages.filter(
      (msg) => !messageIdsToDelete.includes(msg._id)
    );
    const fileUrlsToRemove = deletedMessages.flatMap((m) => m.fileUrls ?? []);
    const updatedFiles =
      fileUrlsToRemove.length > 0
        ? prevFiles.filter((file) => !fileUrlsToRemove.includes(file.url))
        : prevFiles;

    return {
      messages: {
        ...state.messages,
        [chatId]: filteredMessages,
      },
      files: {
        ...state.files,
        [chatId]: updatedFiles,
      },
    };
  });

  useChatStore.setState((state) => {
    const chatObj = state.chats.find((c) => c._id === chatId);
    if (!chatObj) return state;

    const lastMessageId = chatObj.lastMessage?._id;
    if (!lastMessageId) return state;

    const lastMessageDeleted = messageIdsToDelete.includes(lastMessageId);

    if (!lastMessageDeleted) return state;

    const messages = useMessagesStore.getState().messages[chatId] || [];

    const newLastMessage = [...messages].sort((a, b) => {
      const aTime = new Date(a.sentAt ?? a.createdAt ?? 0).getTime();
      const bTime = new Date(b.sentAt ?? b.createdAt ?? 0).getTime();
      return bTime - aTime;
    })[0];

    return {
      chats: state.chats.map((c) =>
        c._id === chatId ? { ...c, lastMessage: newLastMessage || null } : c
      ),
    };
  });

  if (failedMessages.length > 0) {
    enqueueSnackbar(
      `Удалено ${deletedMessages.length}, не удалось удалить ${failedMessages.length} сообщений`,
      { variant: "warning" }
    );
  } else {
    enqueueSnackbar("Сообщения успешно удалены", { variant: "success" });
  }
};

export const handleMessageUpdateResult = (data: any) => {
  if (!data.success) return;

  const updatedMessage: Message = data.result;
  const chatId = updatedMessage.chat._id;

  useMessagesStore.setState((state) => {
    const prevMessages = state.messages[chatId] || [];
    const prevFiles = state.files[chatId] || [];

    const oldMessage = prevMessages.find((m) => m._id === updatedMessage._id);
    const oldUrls = oldMessage?.fileUrls ?? [];
    const newUrls = updatedMessage.fileUrls ?? [];

    const updatedMessages = prevMessages.map((msg) =>
      msg._id === updatedMessage._id ? updatedMessage : msg
    );
    let updatedFiles = [...prevFiles];

    // Удаляем старые ссылки, если они были
    if (oldUrls.length > 0) {
      updatedFiles = updatedFiles.filter(
        (file) => !oldUrls.includes(file.url)
      );
    }

    // Добавляем новые ссылки в виде объектов MessageFile
    if (newUrls.length > 0) {
      const newFileObjects: MessageFile[] = newUrls.map((url) => ({
        url,
        msgId: updatedMessage._id,
        createdAt: updatedMessage.createdAt,
      }));

      // Избегаем дублей
      const mergedFiles = [
        ...updatedFiles,
        ...newFileObjects.filter(
          (nf) =>
            !updatedFiles.some(
              (f) => f.url === nf.url && f.msgId === nf.msgId
            )
        ),
      ];

      updatedFiles = mergedFiles;
    }

    return {
      messages: {
        ...state.messages,
        [chatId]: updatedMessages,
      },
      files: {
        ...state.files,
        [chatId]: updatedFiles,
      },
      loadingMessage: {
        ...state.loadingMessage,
        [updatedMessage._id]: false,
      },
    };
  });
};

export const handleErrorValidation = (data: any) => {
  const chatId = data?.rawPayload?.chat;
  const sentAt = data?.rawPayload?.sentAt;

  useMessagesStore.setState((state) => {
    if (!state.messages[chatId]) return state;

    const msgs = state.messages[chatId];
    const msgIndex = msgs.findIndex((m) => m.sentAt === sentAt);
    const msgFinded = msgs.find((m) => m.sentAt === sentAt);

    if (msgIndex === -1) return state;

    const updatedMsgs = [...msgs];
    updatedMsgs[msgIndex] = { ...updatedMsgs[msgIndex], err: true };

    updateTempMessageErrorDB(chatId, sentAt, true);

    return {
      messages: {
        ...state.messages,
        [chatId]: updatedMsgs,
      },
      loadingMessage: {
        ...state.loadingMessage,
        [msgFinded?._id || `temp-${sentAt}`]: false,
      },
    };
  });

  useChatStore.setState((state) => {
    return {
      chats: state.chats.map((chat) => {
        if (chat._id === chatId) {
          const currentMessages = useMessagesStore.getState().messages[chatId];
          const failedMessage = currentMessages?.find(
            (m) => m.sentAt === sentAt
          );

          if (failedMessage) {
            return {
              ...chat,
              lastMessage: failedMessage,
            };
          }
        }
        return chat;
      }),
    };
  });
};

export const handleUserOnlineStatus = ({
  user,
  isOnline,
}: {
  user: string;
  isOnline: boolean;
}) => {
  useChatStore.setState((state) => {
    const chats = state.chats.map((chat) => {
      if (chat.contact.user._id === user) {
        return {
          ...chat,
          contact: {
            ...chat.contact,
            user: {
              ...chat.contact.user,
              isOnline,
            },
          },
        };
      }
      return chat;
    });
    return { chats };
  });
};

export const handleDisconnect = (reason: string) => {
  console.log("Socket.IO disconnected:", reason);
};

export const handleConnectError = (err: any) => {
  console.error("Socket.IO connect error:", err.message);
};
